import axios from 'axios';
import { ApiResponse, Member, Ledger, BankCard, Transaction, Order, Payment } from '../types';

// 创建axios实例
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 可以在这里添加token等认证信息
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// 会员管理相关API
export const memberApi = {
  // 创建会员
  createMember: (memberData: Partial<Member>): Promise<ApiResponse<Member>> => {
    return api.post('/member/create', memberData);
  },
  
  // 获取会员信息
  getMember: (memberId: string): Promise<ApiResponse<Member>> => {
    return api.get(`/member/${memberId}`);
  },
  
  // 更新会员信息
  updateMember: (memberId: string, memberData: Partial<Member>): Promise<ApiResponse<Member>> => {
    return api.put(`/member/${memberId}`, memberData);
  },
  
  // 会员列表查询
  getMemberList: (params?: any): Promise<ApiResponse<Member[]>> => {
    return api.get('/member/list', { params });
  },
};

// 登记簿相关API
export const ledgerApi = {
  // 创建登记簿
  createLedger: (ledgerData: any): Promise<ApiResponse<Ledger>> => {
    return api.post('/ledger/create', ledgerData);
  },
  
  // 获取登记簿信息
  getLedger: (ledgerId: string): Promise<ApiResponse<Ledger>> => {
    return api.get(`/ledger/${ledgerId}`);
  },
  
  // 获取登记簿余额
  getLedgerBalance: (ledgerId: string): Promise<ApiResponse<{ balance: string; frozen_balance: string }>> => {
    return api.get(`/ledger/${ledgerId}/balance`);
  },
  
  // 登记簿列表查询
  getLedgerList: (params?: any): Promise<ApiResponse<Ledger[]>> => {
    return api.get('/ledger/list', { params });
  },
};

// 银行卡管理相关API
export const bankCardApi = {
  // 绑定银行卡
  bindCard: (cardData: Partial<BankCard>): Promise<ApiResponse<BankCard>> => {
    return api.post('/bankcard/bind', cardData);
  },
  
  // 获取银行卡信息
  getCard: (cardId: string): Promise<ApiResponse<BankCard>> => {
    return api.get(`/bankcard/${cardId}`);
  },
  
  // 银行卡列表
  getCardList: (memberId: string): Promise<ApiResponse<BankCard[]>> => {
    return api.get(`/bankcard/list/${memberId}`);
  },
  
  // 小额验证
  verifyCard: (cardId: string, amount: string): Promise<ApiResponse<any>> => {
    return api.post(`/bankcard/${cardId}/verify`, { amount });
  },
  
  // 解绑银行卡
  unbindCard: (cardId: string): Promise<ApiResponse<any>> => {
    return api.delete(`/bankcard/${cardId}`);
  },
};

// 交易相关API
export const transactionApi = {
  // 获取交易流水
  getTransactionList: (params?: any): Promise<ApiResponse<Transaction[]>> => {
    return api.get('/transaction/list', { params });
  },
  
  // 获取交易详情
  getTransaction: (transactionId: string): Promise<ApiResponse<Transaction>> => {
    return api.get(`/transaction/${transactionId}`);
  },
  
  // 充值
  recharge: (data: { ledger_id: string; amount: string }): Promise<ApiResponse<any>> => {
    return api.post('/transaction/recharge', data);
  },
  
  // 提现
  withdraw: (data: { ledger_id: string; card_id: string; amount: string }): Promise<ApiResponse<any>> => {
    return api.post('/transaction/withdraw', data);
  },
  
  // 确认充值
  confirmRecharge: (transactionId: string): Promise<ApiResponse<any>> => {
    return api.post(`/transaction/${transactionId}/confirm-recharge`);
  },
  
  // 同步余额
  syncBalance: (ledgerId: string): Promise<ApiResponse<any>> => {
    return api.post(`/ledger/${ledgerId}/sync-balance`);
  },
  
  // 重置交易密码
  resetPassword: (data: { ledgerId: string; memberId: string }): Promise<ApiResponse<any>> => {
    return api.post('/transaction/reset-password', data);
  },
};

// 支付相关API
export const paymentApi = {
  // 创建支付订单
  createPayment: (orderData: Partial<Order>): Promise<ApiResponse<Payment>> => {
    return api.post('/payment/create', orderData);
  },
  
  // 获取支付二维码
  getPaymentQRCode: (orderId: string): Promise<ApiResponse<{ qr_code_url: string }>> => {
    return api.get(`/payment/${orderId}/qrcode`);
  },
  
  // 查询支付状态
  getPaymentStatus: (orderId: string): Promise<ApiResponse<Payment>> => {
    return api.get(`/payment/${orderId}/status`);
  },
  
  // 确认支付
  confirmPayment: (orderId: string): Promise<ApiResponse<any>> => {
    return api.post(`/payment/${orderId}/confirm`);
  },
};

// 文件上传API
export const uploadApi = {
  // 上传文件
  uploadFile: (file: File): Promise<ApiResponse<{ file_id: string; file_url: string }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// 联行号查询API
export const bankCodeApi = {
  // 搜索银行
  searchBanks: (keyword: string): Promise<ApiResponse<Array<{ code: string; name: string }>>> => {
    return api.get('/bankcode/search', { params: { keyword } });
  },
  
  // 根据行号获取行名
  getBankName: (bankCode: string): Promise<ApiResponse<{ name: string }>> => {
    return api.get(`/bankcode/${bankCode}`);
  },
};

export default api;